import { Trans } from 'react-i18next'
import styled from 'styled-components'

import { DomUtil } from '@root/shared/core'
import { useTranslation, type TransKey } from '@root/shared/localization'
import { Popover } from '@root/shared/ui-kit'

import { GAS_VARIANT_LABEL_MAP } from '../constants'

import HIGH_IMG from './assets/high.inline.svg'
import LOW_IMG from './assets/low.inline.svg'
import MEDIUM_IMG from './assets/medium.inline.svg'
import { GasSettingsPopoverMetrics } from './gas-settings-popover-metrics'

import type { SuggestedGasSettings } from '../../model'
import type { PopoverProps } from '@root/shared/ui-kit'

type GasSettingsPopoverProps = Pick<
  PopoverProps,
  'open' | 'children' | 'onOpenChange'
> & {
  settings: SuggestedGasSettings.SuggestedSettings | undefined
  variant: SuggestedGasSettings.SettingVariant | 'advanced'
}

export const GasSettingsPopover: React.FC<GasSettingsPopoverProps> = ({
  children,
  settings,
  variant,
  onOpenChange,
  open,
}) => {
  const { t } = useTranslation()
  const renderContent = () => {
    const labelKey = GAS_VARIANT_LABEL_MAP[variant]
    const descriptionKey = GAS_VARIANT_DESCRIPTION_MAP[variant]

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const TransHack = Trans as any

    const descriptionEl = (
      <Description>
        <TransHack
          t={t}
          i18nKey={descriptionKey}
          values={{ label: t(labelKey) }}
          components={{ b: <b /> }}
        />
      </Description>
    )

    if (variant === 'advanced') {
      return descriptionEl
    }

    const renderFees = () => {
      if (!settings) {
        return undefined
      }

      return <GasSettingsPopoverMetrics settings={settings} />
    }

    return (
      <>
        <Title>{t(labelKey)}</Title>
        <Image $variant={variant} />
        {descriptionEl}
        {renderFees()}
      </>
    )
  }

  return (
    <Root open={open} onOpenChange={onOpenChange} content={renderContent()}>
      {children}
    </Root>
  )
}

const Root = styled(Popover)`
  padding-inline: 12px;
  padding-block: 12px 10px;

  display: grid;
  width: 200px;
  z-index: 20;
`

const GAS_VARIANT_CHART_MAP: Record<
  SuggestedGasSettings.SettingVariant,
  string
> = {
  high: HIGH_IMG,
  market: MEDIUM_IMG,
  low: LOW_IMG,
}

const Image = styled.div<{ $variant: SuggestedGasSettings.SettingVariant }>`
  margin-bottom: 10px;
  margin-inline: -12px;
  height: 50px;
  background-position: center center;
  background-size: contain;
  background-repeat: no-repeat;
  background: ${({ $variant }) =>
    DomUtil.cssUrl(GAS_VARIANT_CHART_MAP[$variant])};
`

const Title = styled.div`
  color: var(--text-12);
  text-align: center;
  font-size: 14px;
  font-weight: 700;
  line-height: 24px;

  margin-bottom: 10px;
`

const Description = styled.div`
  color: var(--text-12);
  text-align: center;
  font-size: 12px;
  font-weight: 400;
  line-height: 15px;
  & b {
    font-weight: 700;
  }
`

const GAS_VARIANT_DESCRIPTION_MAP: Record<
  SuggestedGasSettings.SettingVariant | 'advanced',
  TransKey
> = {
  high: 'gas-settings.tooltip-description.high',
  market: 'gas-settings.tooltip-description.market',
  low: 'gas-settings.tooltip-description.low',
  advanced: 'gas-settings.tooltip-description.advanced',
}
